// @ts-check

/**
 * @module files/addFilesToCourse
 */

import { openFilePicker, openLinkingFilePicker, pickerPurpose } from '../frames/windowOpen';
import CloudFile from './CloudFile';
import CourseFile from './CourseFile';
import { validateFile, validateFileName, fileErrors } from './validation';
import { uploadFileToFileRepo, uploadFileFromExternalSourceToFileRepo, getOneTimeToken } from '../network/fileRepo';
import { post } from '../network/fetch';

/**
 * Opens a popup to pickerUrl and handles the file or files picked by adding them
 * as elements to the course specified with courseId and folder folderId. The picker
 * should post back google cloud files and oAuthContext to be used with
 * ../frames/windowOpen.openFilePicker.
 *
 * Calls fileStatusCallback whenever a file's status changes with { name, error, success }
 *
 * @param {number} courseId
 * @param {number} folderId
 * @param {string} pickerUrl Page url for the picker to launch
 * @param {boolean} selectMultiple Allow the user to select multiple files
 * @param {object} validationSettings Settings for file validation
 * @param {string[]} validationSettings.invalidExtensions
 * @param {string[]} validationSettings.invalidFileNameCharacters
 * @param {number} validationSettings.maxFileNameLength
 * @param {number} validationSettings.maxFileSizeBytes
 * @param {object} antiForgeryHeaderSettings
 * @param {string} antiForgeryHeaderSettings.headerName
 * @param {string} antiForgeryHeaderSettings.headerValue
 * @param {string} addCloudFilesToCourseFolderEndpoint
 * @param {function} fileStatusCallback Function called whenever one of
 * the picked files' status changes
 * @returns {Promise<CourseFile[]>} CourseFiles created
 */
export function addGoogleDriveFiles(
    courseId,
    folderId,
    pickerUrl,
    selectMultiple,
    validationSettings,
    antiForgeryHeaderSettings,
    addCloudFilesToCourseFolderEndpoint,
    fileStatusCallback
) {
    const popupUrl = new URL(pickerUrl, window.location.origin);

    return openLinkingFilePicker(popupUrl.href, selectMultiple)
        .then(({ oAuthContext, files }) => {
            const cloudFiles = files.map(f => CloudFile.fromGoogle(f, oAuthContext));

            return addCloudFilesToCourseFolder(
                courseId,
                folderId,
                cloudFiles,
                validationSettings,
                antiForgeryHeaderSettings,
                addCloudFilesToCourseFolderEndpoint,
                fileStatusCallback
            );
        });
}

/**
 * Opens a popup to pickerUrl and handles the file or files picked by adding them
 * as elements to the course specified with courseId and folder folderId. The picker
 * should post back oneDrive cloud files and oAuthContext to be used with
 * ../frames/windowOpen.openFilePicker.
 *
 * Calls fileStatusCallback whenever a file's status changes with { name, error, success }
 *
 * @param  {number} courseId
 * @param  {number} folderId
 * @param {string} pickerUrl Page url for the picker to launch
 * @param {boolean} selectMultiple Allow the user to select multiple files
 * @param {object} validationSettings Settings for file validation
 * @param {string[]} validationSettings.invalidExtensions
 * @param {string[]} validationSettings.invalidFileNameCharacters
 * @param {number} validationSettings.maxFileNameLength
 * @param {number} validationSettings.maxFileSizeBytes
 * @param {object} antiForgeryHeaderSettings
 * @param {string} antiForgeryHeaderSettings.headerName
 * @param {string} antiForgeryHeaderSettings.headerValue
 * @param {string} addCloudFilesToCourseFolderEndpoint
 * @param {function} fileStatusCallback Function called whenever one of the picked files' status changes
 * @param {boolean} enableUploadFromOneDrive Allow file to be uploaded from OneDrive to FileRepo
 * @param {boolean} enableLinkingFromOneDrive Allow file to be linked from OneDrive
 * @returns {Promise<CourseFile[]>} CourseFiles created
 */
export function addOneDriveFiles(
    courseId,
    folderId,
    pickerUrl,
    selectMultiple,
    validationSettings,
    antiForgeryHeaderSettings,
    fileRepoSettings,
    addCloudFilesToCourseFolderEndpoint,
    addFileRepoFilesToCourseFolderEndpoint,
    fileStatusCallback,
    enableUploadFromOneDrive = true,
    enableLinkingFromOneDrive = true
) {
    const popupUrl = new URL(pickerUrl, window.location.origin);

    let purpose = pickerPurpose.Combined;

    if (!enableUploadFromOneDrive && enableLinkingFromOneDrive) {
        purpose = pickerPurpose.LinkToCloud;
    } else if (enableUploadFromOneDrive && !enableLinkingFromOneDrive) {
        purpose = pickerPurpose.Upload;
    }

    return openFilePicker(popupUrl.href, purpose, selectMultiple)
        .then(({ oAuthContext, files, isLinkMode }) => {
            if (isLinkMode) {
                const cloudFiles = files.map(f => CloudFile.fromOneDrive(f, oAuthContext));

                return addCloudFilesToCourseFolder(
                    courseId,
                    folderId,
                    cloudFiles,
                    validationSettings,
                    antiForgeryHeaderSettings,
                    addCloudFilesToCourseFolderEndpoint,
                    fileStatusCallback
                );
            } else {
                const uploadMethod = (file) => {
                    return updateFileRepoToken(file, fileRepoSettings).then(fileRepoSettings => {
                        return uploadFileFromExternalSourceToFileRepo(file.id, file.url, file.name, file.size, file.sourceType, fileRepoSettings);
                    });
                };

                return addFilesViaFileRepo(
                    courseId,
                    folderId,
                    files,
                    validationSettings,
                    antiForgeryHeaderSettings,
                    uploadMethod,
                    addFileRepoFilesToCourseFolderEndpoint,
                    fileStatusCallback
                );
            }
        });
}

function addCloudFilesToCourseFolder(
    courseId,
    folderId,
    cloudFiles,
    validationSettings,
    antiForgeryHeaderSettings,
    addCloudFilesToCourseFolderEndpoint,
    fileStatusCallback
) {
    cloudFiles = cloudFiles.filter(file => {
        fileStatusCallback(file);

        const validationResult = validateFileName(
            file.name,
            validationSettings.invalidExtensions,
            [], // linked cloud files don't need to check invalidFileNameCharacters
            validationSettings.maxFileNameLength
        );

        if (!validationResult.valid) {
            fileStatusCallback({ name: file.name, error: validationResult.error });
        }

        return validationResult.valid;
    });

    return postCloudFilesToCourseEndpoint(
        courseId,
        folderId,
        cloudFiles,
        addCloudFilesToCourseFolderEndpoint,
        antiForgeryHeaderSettings
    ).then(courseFiles => {
        courseFiles.forEach(file => {
            fileStatusCallback(file);
        });

        courseFiles.forEach((courseFile) => {
            const file = cloudFiles.find(cloudFile => cloudFile.id === courseFile.cloudId);

            if (file) {
                courseFile.cloudFile = file;
            }
        });

        return courseFiles;
    }).catch(error => {
        cloudFiles.forEach(file => {
            fileStatusCallback({ name: file.name, error: fileErrors.unknown });
        });

        throw error;
    });
}

function postCloudFilesToCourseEndpoint(
    courseId,
    folderId,
    cloudFiles,
    addCloudFilesToCourseFolderEndpoint,
    antiForgeryHeaderSettings
) {
    const apiEndpoint = addCloudFilesToCourseFolderEndpoint
        .replace('{courseId}', courseId)
        .replace('{folderId}', folderId);

    const headers = { [antiForgeryHeaderSettings.headerName]: antiForgeryHeaderSettings.headerValue };

    return post(apiEndpoint, cloudFiles, { headers }).then(response => {
        return response.map(file => CourseFile.fromApi(file));
    });
}

/**
 * Adds a cloud file to a course (courseId) in a folder (folderId) using some
 * of the CloudFile's parameters.
 *
 * Calls fileStatusCallback whenever a file's status changes with { name, error, success }
 *
 * @param {number} courseId
 * @param {number} folderId
 * @param {object} file
 * @param {string} file.viewUrl
 * @param {any} file.id
 * @param {string} file.mimeType
 * @param {string} file.name
 * @param {any} file.oAuthContext
 * @param {string} file.sourceType
 * @param {object} validationSettings Settings for file validation
 * @param {string[]} validationSettings.invalidExtensions
 * @param {string[]} validationSettings.invalidFileNameCharacters
 * @param {number} validationSettings.maxFileNameLength
 * @param {number} validationSettings.maxFileSizeBytes
 * @param {object} antiForgeryHeaderSettings
 * @param {string} antiForgeryHeaderSettings.headerName
 * @param {string} antiForgeryHeaderSettings.headerValue
 * @param {string} addCloudFilesToCourseFolderEndpoint
 * @param {function} fileStatusCallback Function called whenever one of
 * the picked files' status changes
 * @returns {Promise<CourseFile[]>} CourseFiles created
 */
export function addRecentCloudFile(
    courseId,
    folderId,
    file,
    validationSettings,
    antiForgeryHeaderSettings,
    addCloudFilesToCourseFolderEndpoint,
    fileStatusCallback
) {
    const cloudFile = new CloudFile({
        downloadUrl: null,
        editUrl: file.viewUrl,
        id: file.id,
        isRegisteredWithCasper: false,
        isSupportedByCasper: true,
        isTemplate: false,
        mimeType: file.mimeType,
        name: file.name,
        oAuthContext: file.oAuthContext,
        sourceType: file.sourceType,
        viewUrl: file.viewUrl,
    });

    return addCloudFilesToCourseFolder(
        courseId,
        folderId,
        [cloudFile],
        validationSettings,
        antiForgeryHeaderSettings,
        addCloudFilesToCourseFolderEndpoint,
        fileStatusCallback
    );
}

/**
 * Adds the provided file repo files to a course (courseId) in a folder (folderId)
 * and returns the response as a collection of CourseFiles.
 *
 * Calls fileStatusCallback whenever a file's status changes with { name, error, success }
 *
 * @param {any} courseId
 * @param {any} folderId
 * @param {any} files
 * @param {string} addFileRepoFilesToCourseFolderEndpoint
 * @param {object} antiForgeryHeaderSettings
 * @param {string} antiForgeryHeaderSettings.headerName
 * @param {string} antiForgeryHeaderSettings.headerValue
 * @returns {Promise<CourseFile[]>} Result of the post as CourseFiles
 */
export function addFileRepoFilesToCourse(
    courseId,
    folderId,
    files,
    addFileRepoFilesToCourseFolderEndpoint,
    antiForgeryHeaderSettings
) {
    const apiEndpoint = addFileRepoFilesToCourseFolderEndpoint
        .replace('{courseId}', courseId)
        .replace('{folderId}', folderId);

    const headers = { [antiForgeryHeaderSettings.headerName]: antiForgeryHeaderSettings.headerValue };

    return post(apiEndpoint, files, { headers }).then(response => {
        return response.map(file => CourseFile.fromApi(file));
    });
}

function addFilesViaFileRepo(
    courseId,
    folderId,
    files,
    validationSettings,
    antiForgeryHeaderSettings,
    uploadToFileRepoMethod,
    addFileRepoFilesToCourseFolderEndpoint,
    fileStatusCallback
) {
    const successfulFiles = [];

    files.forEach(file => fileStatusCallback(file));

    files = files.filter(file => {
        const validation = validateFile(file, validationSettings);

        if (!validation.valid) {
            fileStatusCallback({ name: file.name, error: validation.error });
        }

        return validation.valid;
    });

    const addExternalFileAndContinue = function (resolve) {
        if (files.length === 0) {
            resolve();

            return;
        }

        const file = files.pop();

        uploadToFileRepoMethod(file).then(fileRepoFile => {
            if (fileRepoFile.error) {
                fileStatusCallback({ ...fileRepoFile, name: file.name });

                addExternalFileAndContinue(resolve);

                return;
            }

            addFileRepoFilesToCourse(
                courseId,
                folderId,
                [fileRepoFile],
                addFileRepoFilesToCourseFolderEndpoint,
                antiForgeryHeaderSettings
            ).then(courseFiles => {
                courseFiles.forEach(courseFile => {
                    fileStatusCallback(courseFile);
                    courseFile.fileRepoFile = fileRepoFile;
                    successfulFiles.push(courseFile);
                });

                addExternalFileAndContinue(resolve);
            });
        }).catch(() => {
            fileStatusCallback({ name: file.name, error: fileErrors.unknown });

            addExternalFileAndContinue(resolve);
        });
    };

    const worker1 = new Promise(resolve => addExternalFileAndContinue(resolve));
    const worker2 = new Promise(resolve => addExternalFileAndContinue(resolve));

    return Promise.all([worker1, worker2]).then(() => {
        return successfulFiles;
    });
}

/**
 * Uploads local files to file repo, then adds the resulting file repo files to
 * a course in the specified folder. Returns files added as CourseFiles.
 *
 * Calls fileStatusCallback whenever a file's status changes with { name, error, success }
 *
 * @param {number} courseId
 * @param {number} folderId
 * @param {File[]} files
 * @param {object} validationSettings Settings for file validation
 * @param {string[]} validationSettings.invalidExtensions
 * @param {string[]} validationSettings.invalidFileNameCharacters
 * @param {number} validationSettings.maxFileNameLength
 * @param {number} validationSettings.maxFileSizeBytes
 * @param {object} antiForgeryHeaderSettings
 * @param {string} antiForgeryHeaderSettings.headerName
 * @param {string} antiForgeryHeaderSettings.headerValue
 * @param {object} fileRepoSettings File repo connection and authorization parameters
 * @param {string} [fileRepoSettings.endpoint]
 * @param {string} [fileRepoSettings.tokenProviderEndpoint]
 * @param {string} [fileRepoSettings.authorizationHeaderKey]
 * @param {string} [fileRepoSettings.token]
 * @param {string} [fileRepoSettings.validationHeaderKey]
 * @param {string} [fileRepoSettings.validationHeader]
 * @param {string} [fileRepoSettings.publicUploadHeaderKey]
 * @param {string} [fileRepoSettings.publicUploadHeader]
 * @param {string} addFileRepoFilesToCourseFolderEndpoint
 * @param {function} fileStatusCallback Function called whenever one of
 * the picked files' status changes
 * @returns {Promise<CourseFile[]>} Files added to course as CourseFiles
 */
export function addLocalFiles(
    courseId,
    folderId,
    files,
    validationSettings,
    antiForgeryHeaderSettings,
    fileRepoSettings,
    addFileRepoFilesToCourseFolderEndpoint,
    fileStatusCallback
) {
    const uploadMethod = (file) => {
        return updateFileRepoToken(file, fileRepoSettings).then(fileRepoSettings => {
            return uploadFileToFileRepo(file, fileRepoSettings);
        });
    };

    return addFilesViaFileRepo(
        courseId,
        folderId,
        files,
        validationSettings,
        antiForgeryHeaderSettings,
        uploadMethod,
        addFileRepoFilesToCourseFolderEndpoint,
        fileStatusCallback
    );
}

function updateFileRepoToken(file, fileRepoSettings) {
    return getOneTimeToken(file, fileRepoSettings.tokenProviderEndpoint, fileRepoSettings.token)
        .then(token => {
            fileRepoSettings.token = token;

            return fileRepoSettings;
        });
}
